// src/features/mocktests/components/SpeakingPage.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box, CircularProgress, Typography } from "@mui/material";
import {
  useLazyGetPracticeTestDataQuery,
  useUpdateTestAttemptMutation,
  useLazyGetInstructionsQuery,
  useAcknowledgeInstructionMutation,
  useLazyGetTestAttemptResultsQuery,
} from "../mockTestSlice";
import SpeakingSection from "./SpeakingSection";
import TestResultsPopup from "./TestResultsPopup";
import {
  InstructionWrapper,
  useInstructions,
} from "../../../components/Instructions";

export default function SpeakingPage() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // --- Instruction system integration ---
  const {
    instructionState,
    showInstructionModal,
    hideInstructionModal,
    showConfirmationDialog,
    hideConfirmationDialog,
    showUnattemptedDialog,
    hideUnattemptedDialog,
    showTimeExpiryDialog,
    hideTimeExpiryDialog,
  } = useInstructions();

  // Show Speaking instructions at section start
  const [showInitialInstructions, setShowInitialInstructions] = useState(true);
  const [instructionsAcknowledged, setInstructionsAcknowledged] =
    useState(false);

  const [allSpeakingAudios, setAllSpeakingAudios] = useState([]);
  const [speakingTestCompleted, setSpeakingTestCompleted] = useState(false);

  const [currentPart, setCurrentPart] = useState(1);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [answers, setAnswers] = useState({});
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [speakingCardIds, setSpeakingCardIds] = useState([]);
  const [localSpeakingData, setLocalSpeakingData] = useState(null);

  const studentAttemptId = location.state?.studentAttemptId;
  const speakingTestId =
    location.state?.speakingTestId ||
    location.state?.mockTest?.ieltsTest?.speakingId ||
    location.state?.mockTest?.oetTest?.speakingId;

  // Check for essential state and redirect if missing (browser refresh)
  useEffect(() => {
    if (!studentAttemptId || !location.state) {
      console.log(
        "Essential state missing, redirecting to main mock test screen"
      );
      // Use testId from location state if available, otherwise redirect to dashboard
      const redirectTestId = location.state?.testId || testId;
      if (redirectTestId) {
        navigate(`/mock-test/${redirectTestId}`, { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    }
  }, [studentAttemptId, location.state, navigate, testId]);

  // REMOVE MODAL state, hardcode isAiBased
  const isAiBased = true; // <<--- FORCE AI always!

  // Get speaking part IDs from location (IELTS structure)
  useEffect(() => {
    // Priority order: direct speakingPartIds -> IELTS test -> OET test (fallback)
    if (
      Array.isArray(location.state?.speakingPartIds) &&
      location.state.speakingPartIds.length > 0
    ) {
      setSpeakingCardIds(location.state.speakingPartIds);
    } else if (
      Array.isArray(location.state?.mockTest?.ieltsTest?.speakingPartIds) &&
      location.state.mockTest.ieltsTest.speakingPartIds.length > 0
    ) {
      setSpeakingCardIds(location.state.mockTest.ieltsTest.speakingPartIds);
    } else if (
      Array.isArray(location.state?.mockTest?.oetTest?.speakingCardIds) &&
      location.state.mockTest.oetTest.speakingCardIds.length > 0
    ) {
      setSpeakingCardIds(location.state.mockTest.oetTest.speakingCardIds);
    } else if (
      Array.isArray(location.state?.speakingCardIds) &&
      location.state.speakingCardIds.length > 0
    ) {
      setSpeakingCardIds(location.state.speakingCardIds);
    }
  }, [location.state]);

  const [triggerGetPracticeTestData, { data: practiceData, isLoading, error }] =
    useLazyGetPracticeTestDataQuery();
  const [updateTestAttempt] = useUpdateTestAttemptMutation();
  const [getTestAttemptResults] = useLazyGetTestAttemptResultsQuery();

  // Test Results Popup state
  const [testResults, setTestResults] = useState(null);
  const [showResultsPopup, setShowResultsPopup] = useState(false);

  // Handler for showing results popup (passed to SpeakingSection)
  const handleShowResultsPopup = async () => {
    try {
      const resultsResponse = await getTestAttemptResults(
        studentAttemptId
      ).unwrap();
      setTestResults(resultsResponse);
      setShowResultsPopup(true);
    } catch (error) {
      console.error("Error fetching results:", error);
      // If fetching fails, navigate to results page directly
      navigate(`/mock-test/${testId}/results`);
    }
  };

  // Instruction API hooks
  const [
    getInstructions,
    { data: instructionData, isLoading: instructionLoading },
  ] = useLazyGetInstructionsQuery();
  const [acknowledgeInstruction] = useAcknowledgeInstructionMutation();

  // Fetch and show instructions immediately when page loads
  useEffect(() => {
    if (showInitialInstructions && studentAttemptId) {
      // Fetch instructions from API
      getInstructions({
        studentAttemptId,
        module: "speaking",
      });
      const mode = location.state?.isPracticeMode ? "practice" : "simulation";
      showInstructionModal("speaking", null, mode);
      setShowInitialInstructions(false);
    }
  }, [
    showInitialInstructions,
    studentAttemptId,
    getInstructions,
    showInstructionModal,
    location.state,
  ]);

  // Guard: Only fetch when all params are ready and test is not completed
  useEffect(() => {
    // Don't fetch data until instructions are acknowledged
    if (!instructionsAcknowledged) {
      return;
    }

    console.log("SpeakingPage useEffect triggered", {
      speakingTestCompleted,
      studentAttemptId,
      speakingTestId,
      speakingCardIds: speakingCardIds.length,
      currentCardIndex,
      cardId: speakingCardIds[currentCardIndex],
    });

    if (
      speakingTestCompleted ||
      !studentAttemptId ||
      !speakingTestId ||
      !Array.isArray(speakingCardIds) ||
      speakingCardIds.length === 0 ||
      !speakingCardIds[currentCardIndex]
    ) {
      console.log("SpeakingPage useEffect early return");
      return;
    }

    console.log("SpeakingPage calling API with params:", {
      studentAttemptId,
      speakingId: speakingTestId,
      currentSection: "speaking",
      speakingCardId: speakingCardIds[currentCardIndex],
    });
    setLocalSpeakingData(null);

    triggerGetPracticeTestData({
      studentAttemptId,
      speakingId: speakingTestId,
      currentSection: "speaking",
      speakingCardId: speakingCardIds[currentCardIndex],
    });
    // eslint-disable-next-line
  }, [
    studentAttemptId,
    speakingTestId,
    speakingCardIds,
    currentCardIndex,
    speakingTestCompleted,
    instructionsAcknowledged,
    triggerGetPracticeTestData,
  ]);

  useEffect(() => {
    if (practiceData) {
      setLocalSpeakingData(practiceData);
    }
  }, [practiceData]);

  // Extract speaking submission status from API data
  const isSpeakingSubmitted = localSpeakingData?.data?.isSpeakingSubmitted || 
    practiceData?.data?.isSpeakingSubmitted || 
    false;

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleNextPart = () => {
    setCurrentPart((prev) => prev + 1);
    setCurrentOffset((prev) => prev + 1);
    setAnswers({});
    setCurrentCardIndex((prev) => prev + 1);
  };

  const transformSpeakingData = (apiData) => {
    let speakingTest = null;
    if (apiData?.data?.data?.speakingTest) {
      speakingTest = apiData.data.data.speakingTest;
    } else if (apiData?.data?.speakingTest) {
      speakingTest = apiData.data.speakingTest;
    }
    if (!speakingTest) return null;

    console.log("Transforming speaking data:", {
      speakingTest,
      currentPart,
      totalParts: speakingCardIds.length,
    });

    return {
      speakingTest,
      currentPart,
      totalParts: speakingCardIds.length || 4,
    };
  };

  // Show loading only if not completed and no data
  if (
    (isLoading || (!localSpeakingData && instructionsAcknowledged)) &&
    !speakingTestCompleted
  ) {
    return (
      <>
        {/* Instruction System Wrapper - must render even during loading */}
        <InstructionWrapper
          instructionState={instructionState}
          onInstructionNext={async () => {
            // Acknowledge instruction when user clicks Next
            if (studentAttemptId) {
              try {
                await acknowledgeInstruction({
                  studentAttemptId,
                  module: "speaking",
                }).unwrap();
                setInstructionsAcknowledged(true);
              } catch (error) {
                console.error("Failed to acknowledge instruction:", error);
              }
            }
            hideInstructionModal();
          }}
          onInstructionClose={async () => {
            // Acknowledge instruction when modal closes
            if (studentAttemptId) {
              try {
                await acknowledgeInstruction({
                  studentAttemptId,
                  module: "speaking",
                }).unwrap();
                setInstructionsAcknowledged(true);
              } catch (error) {
                console.error("Failed to acknowledge instruction:", error);
              }
            }
            hideInstructionModal();
          }}
          onConfirmationConfirm={() => {
            hideConfirmationDialog();
            hideUnattemptedDialog();
          }}
          onConfirmationClose={hideConfirmationDialog}
          onUnattemptedContinue={() => {
            hideUnattemptedDialog();
          }}
          onUnattemptedClose={() => {
            hideUnattemptedDialog();
          }}
          onTimeExpiryContinue={hideTimeExpiryDialog}
          timeRemaining={null}
          unattemptedCount={0}
          nextSection="results"
          instructionData={instructionData?.data}
          instructionLoading={instructionLoading}
        />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <CircularProgress size={60} thickness={5} />
          <Typography sx={{ ml: 2 }}>Loading speaking data...</Typography>
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography color="error">
          Error loading speaking test: {error.message}
        </Typography>
      </Box>
    );
  }

  // If test is completed, we can proceed even without localSpeakingData
  const speakingData = localSpeakingData
    ? transformSpeakingData(localSpeakingData)
    : null;

  // Only show "no data" message if we're not completed and have no data
  if (
    !speakingData &&
    !isLoading &&
    localSpeakingData &&
    !speakingTestCompleted
  ) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography>No speaking data available</Typography>
      </Box>
    );
  }

  // ====== No modal, just always pass AI ======
  return (
    <>
      {/* Instruction System Wrapper */}
      <InstructionWrapper
        instructionState={instructionState}
        onInstructionNext={async () => {
          // Acknowledge instruction when user clicks Next
          if (studentAttemptId) {
            try {
              await acknowledgeInstruction({
                studentAttemptId,
                module: "speaking",
              }).unwrap();
              setInstructionsAcknowledged(true);
            } catch (error) {
              console.error("Failed to acknowledge instruction:", error);
            }
          }
          hideInstructionModal();
        }}
        onInstructionClose={async () => {
          // Acknowledge instruction when modal closes
          if (studentAttemptId) {
            try {
              await acknowledgeInstruction({
                studentAttemptId,
                module: "speaking",
              }).unwrap();
              setInstructionsAcknowledged(true);
            } catch (error) {
              console.error("Failed to acknowledge instruction:", error);
            }
          }
          hideInstructionModal();
        }}
        onConfirmationConfirm={() => {
          hideConfirmationDialog();
          hideUnattemptedDialog();
        }}
        onConfirmationClose={hideConfirmationDialog}
        onUnattemptedContinue={() => {
          hideUnattemptedDialog();
        }}
        onUnattemptedClose={() => {
          hideUnattemptedDialog();
        }}
        onTimeExpiryContinue={hideTimeExpiryDialog}
        timeRemaining={null}
        unattemptedCount={0}
        nextSection="results"
        instructionData={instructionData?.data}
        instructionLoading={instructionLoading}
      />
      <div>
        <SpeakingSection
          speakingData={speakingData}
          currentPart={currentPart}
          answers={answers}
          onAnswerChange={handleAnswerChange}
          onNextPart={handleNextPart}
          studentAttemptId={studentAttemptId}
          speakingTestId={speakingTestId}
          setLocalSpeakingData={setLocalSpeakingData}
          setCurrentPart={setCurrentPart}
          setCurrentOffset={setCurrentOffset}
          navigate={navigate}
          location={location}
          dispatch={dispatch}
          speakingCardIds={speakingCardIds}
          currentCardIndex={currentCardIndex}
          setCurrentCardIndex={setCurrentCardIndex}
          allSpeakingAudios={allSpeakingAudios}
          setAllSpeakingAudios={setAllSpeakingAudios}
          isAiBased={isAiBased} // Always true!
          updateTestAttempt={updateTestAttempt}
          setSpeakingTestCompleted={setSpeakingTestCompleted} // Add completion flag setter
          speakingTestCompleted={speakingTestCompleted} // Pass completion status
          isSpeakingSubmitted={isSpeakingSubmitted} // Pass submission status for disabling
          onShowResultsPopup={handleShowResultsPopup} // Handler to show results popup
          instrOpen={
            instructionState.showInstructions &&
            instructionState.currentSection === "speaking"
          }
        />
      </div>

      {/* Test Results Popup */}
      <TestResultsPopup
        open={showResultsPopup}
        onClose={() => setShowResultsPopup(false)}
        resultsData={testResults}
        onContinue={() =>
          navigate(location.state?.backRoute || "/app/mocktests")
        }
        attemptId={studentAttemptId}
        mockTestId={testId}
        baseRoute={location.state?.baseRoute || "/mock-test"}
        backRoute={location.state?.backRoute || "/app/mocktests"}
      />
    </>
  );
}
